"use client";

import { Text, Title } from "@mantine/core";
import { joinItemsToList } from "@utils/helpers";
import { capAll } from "../../../../../../lib/strapi/formatters";
import classes from "./SubjectSeo.module.css";

export default function SubjectSeo({
  slTopicTitles,
  hlTopicTitles,
  subjectNameLower,
}: {
  slTopicTitles: string[];
  hlTopicTitles: string[];
  subjectNameLower: string;
}) {
  const subjectName = capAll(subjectNameLower);
  const tips = [
    {
      title: "Start Early",
      content: `The IB ${subjectName} course includes many topics, each with a great deal of content. Unlike most prior education to the IB, you can’t leave things until last minute. It is vital you have reliable resources from the get-go.`,
    },
    {
      title: "Understand Core Concepts",
      content: `Focus on understanding the fundamental principles and concepts rather than memorizing endless facts. Our videos aim to ensure you can grasp the IB content early in your ${subjectName} course.`,
    },
    {
      title: "Reinforce Regularly",
      content: `Ensuring you review your IB ${subjectName} knowledge regularly is important to solidify long-term memory. Utilise a range of resources such as notes, flashcards, questions, cram sheets, and tests. Out site contains all these resources, fully aligned, in one place.`,
    },
    {
      title: "Analyse Your Performance",
      content: `Reflecting on your progress is key to driving success in IB ${subjectName}. Tiber Tutor offers the most comprehensive and advanced analytics dashboard to hone study efforts to target your weakest areas.`,
    },
    {
      title: "Stay Organised",
      content: `Develop a study schedule that allocates time for each ${subjectName} topic, ensuring you cover the entire syllabus well before the IB exams. Using a Syllabus checklist can give you a clear overview of your areas to cover, just like the one built into the Tiber Tutor site.`,
    },
    {
      title: "Engage",
      content: `The IB is a time to make friends and enjoy life outside of your ${subjectName} studies. However, don't get distracted in lessons or disregard your IA. There is lots to cover and you need to stay focused!`,
    },
  ];
  const exams = {
    old: `The final assessment for IB ${subjectName} consists of 3 external examinations. The exams are: Paper 1 (multiple-choice questions), Paper 2 (short answer and extended response), and Paper 3 (options and data-based questions). SL ${subjectName} students have fewer questions in Paper 1 and a shorter Paper 2 compared to HL ${subjectName} students.`,
    new: `The final assessment for IB ${subjectName} consists of 2 external examinations. The exams are: Paper 1 (multiple choice & data analysis questions) and Paper 2 (extended response and data analysis questions). SL ${subjectName} students have fewer questions in both Paper 1 and Paper 2 compared to HL ${subjectName} students.`,
  };
  const options: { [subjectName: string]: string } = {
    biology:
      "Neurobiology and Behaviour, Biotechnology and Bioinformatics, Ecology and Conservation, and Human Physiology",
    chemistry: "Materials, Biochemistry, Energy, and Medicinal Chemistry",
  };
  const isNew = subjectNameLower.includes("2025");
  return (
    <>
      <Title order={3} className={classes.title}>
        IB {subjectName} Course Structure & Content
      </Title>
      <Text className={classes.text}>
        The IB {subjectName} course is divided into Standard Level (SL) and
        Higher Level (HL), with both levels offering a rich exploration of{" "}
        {subjectName} through theoretical and practical components. The
        curriculum is organized around a core syllabus, organised into{" "}
        {slTopicTitles.length} topics. HL students cover{" "}
        {hlTopicTitles.length > 0
          ? `${hlTopicTitles.length} additional`
          : "additional detail within the"}{" "}
        topics
        {isNew
          ? ". The principal change from the previous syllabus is that many topics have been reorganised to create larger topics. However, the content remains very similar."
          : " as well as an “option” topic in their second year."}
        <br></br>
        <br></br>
        The core {subjectName} syllabus encompasses a broad range of topics:{" "}
        {joinItemsToList(slTopicTitles)}. HL IB {subjectName} students explore
        these topics in greater depth
        {hlTopicTitles.length > 0
          ? ` through several
        additional topics: ${joinItemsToList(hlTopicTitles)}`
          : ""}
        . In total SL students study {slTopicTitles.length} topics and HL
        students{" "}
        {hlTopicTitles.length > 0
          ? `study ${slTopicTitles.length + hlTopicTitles.length} topics`
          : "are the same"}
        .
        {isNew
          ? ""
          : ` HL ${subjectName} students are then able to choose one of 4 option
        topics to study, taken as a separate exam: ${options[subjectNameLower]}.
        This decision is commonly elected by the class teacher, but students
        have the freedom to choose to answer any section in the exam.`}
      </Text>
      <Title order={3} className={classes.title}>
        IB {subjectName} Coursework & Exams
      </Title>
      <Text className={classes.text}>
        During their period of study, {subjectName} students undertake an
        individual investigation called the Internal Assessment (IA). Students
        create an original research question with the assistance of their
        teacher and proceed to investigate and produce a 20-page report,
        including an introduction, methodology, analysis, and conclusion. It
        contributes 20% of the final IB {subjectName} grade.
        <br></br>
        <br></br>
        {exams[isNew ? "new" : "old"]}
      </Text>
      <Title order={3} className={classes.title}>
        IB {subjectName} Tips For Success
      </Title>
      <ol>
        {tips.map((t) => (
          <li key={t.title} style={{ lineHeight: "1.6" }}>
            <b>{t.title}:</b>&nbsp;
            {t.content}
          </li>
        ))}
      </ol>
    </>
  );
}
